import { useState } from "react";
import { Save } from "lucide-react";
import type { HolidayRule, HolidayRuleBody } from "@/lib/tickets";
import { Switch } from "@/components/ui/switch";

export const WEEKDAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;

export const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

/**
 * Creating or editing one holiday rule.
 *
 * ⚠️ ONE COMPONENT FOR BOTH TIERS AND BOTH VERBS. The organisation's list and a team's are the
 * same shape and are held to the same server-side validation, so two editors would be two places
 * for the rules to drift — and the create form accepting something the edit form refuses is
 * exactly the kind of difference nobody notices until a rule cannot be corrected.
 *
 * ⚠️ NO FREE-TEXT DATE, anywhere. A typed date is the failure this whole feature exists to
 * avoid: it expires, silently, the year the list runs out.
 *
 * ⚠️ THE OFFSET IS THE SUBTLE CONTROL. "The day after Thanksgiving" must be the Thanksgiving
 * rule plus one day, not "the fourth Friday in November" — those coincide in 2026 and in 2024
 * the fourth Friday falls six days BEFORE Thanksgiving, because that November has five Fridays.
 * The field says so in words, because somebody reaching for the obvious version will otherwise
 * pick it.
 */
export function HolidayEditor({
  existing,
  busy,
  onCancel,
  onSave,
}: {
  existing?: HolidayRule;
  busy: boolean;
  onCancel: () => void;
  onSave: (body: HolidayRuleBody) => Promise<void>;
}) {
  const [name, setName] = useState(existing?.name ?? "");
  const [kind, setKind] = useState<HolidayRule["kind"]>(existing?.kind ?? "FixedDate");
  const [month, setMonth] = useState(existing?.month ?? 1);
  const [day, setDay] = useState(existing?.day ?? 1);
  const [ordinal, setOrdinal] = useState(existing?.ordinal ?? 1);
  const [weekday, setWeekday] = useState(existing?.weekday ?? "Monday");
  const [shift, setShift] = useState(existing?.observedOnNearestWeekday ?? true);
  const [offset, setOffset] = useState(existing?.offsetDays ?? 0);

  return (
    <div className="space-y-3 rounded-lg border border-ring bg-secondary/30 p-3">
      <div>
        <label htmlFor="holiday-name" className="mb-1 block text-xs font-medium">
          Name
        </label>
        <input
          id="holiday-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Day after Thanksgiving"
          className="h-10 w-full max-w-sm rounded-lg border border-input bg-background px-3 text-base focus:border-ring focus:outline-none md:text-sm"
        />
      </div>

      <fieldset>
        <legend className="mb-1 text-xs font-medium">Falls on</legend>
        <div className="flex flex-wrap gap-2">
          {(
            [
              ["FixedDate", "The same date each year"],
              ["NthWeekday", "The nth weekday of a month"],
            ] as const
          ).map(([value, label]) => (
            <label
              key={value}
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm transition hover:border-ring/50"
            >
              <input
                type="radio"
                name="holiday-kind"
                checked={kind === value}
                onChange={() => setKind(value)}
                className="h-4 w-4"
              />
              {label}
            </label>
          ))}
        </div>
      </fieldset>

      <div className="flex flex-wrap items-end gap-2">
        {kind === "NthWeekday" ? (
          <>
            <label className="text-xs">
              <span className="mb-1 block font-medium">Which</span>
              <select
                value={ordinal}
                onChange={(e) => setOrdinal(Number(e.target.value))}
                className="h-10 rounded-lg border border-input bg-background px-2 text-base md:text-sm"
              >
                <option value={1}>1st</option>
                <option value={2}>2nd</option>
                <option value={3}>3rd</option>
                <option value={4}>4th</option>
                <option value={-1}>Last</option>
              </select>
            </label>

            <label className="text-xs">
              <span className="mb-1 block font-medium">Weekday</span>
              <select
                value={weekday ?? "Monday"}
                onChange={(e) => setWeekday(e.target.value)}
                className="h-10 rounded-lg border border-input bg-background px-2 text-base md:text-sm"
              >
                {WEEKDAYS.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </label>
          </>
        ) : (
          <label className="text-xs">
            <span className="mb-1 block font-medium">Day</span>
            <input
              type="number"
              min={1}
              max={31}
              value={day}
              onChange={(e) => setDay(Number(e.target.value))}
              className="h-10 w-20 rounded-lg border border-input bg-background px-2 text-base md:text-sm"
            />
          </label>
        )}

        <label className="text-xs">
          <span className="mb-1 block font-medium">Month</span>
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="h-10 rounded-lg border border-input bg-background px-2 text-base md:text-sm"
          >
            {MONTHS.map((m, i) => (
              <option key={m} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
        </label>

        <label className="text-xs">
          <span className="mb-1 block font-medium">Then shift by</span>
          <select
            value={offset}
            onChange={(e) => setOffset(Number(e.target.value))}
            className="h-10 rounded-lg border border-input bg-background px-2 text-base md:text-sm"
          >
            <option value={-1}>1 day earlier</option>
            <option value={0}>nothing</option>
            <option value={1}>1 day later</option>
            <option value={2}>2 days later</option>
            <option value={3}>3 days later</option>
          </select>
        </label>
      </div>

      {offset !== 0 && (
        <p className="text-xs text-muted-foreground">
          For a holiday defined by another one. &ldquo;Day after Thanksgiving&rdquo; is the 4th
          Thursday in November shifted a day later —{" "}
          <span className="font-medium text-foreground">not</span> the 4th Friday, which in some
          years falls a week before Thanksgiving.
        </p>
      )}

      <div className="flex items-start gap-2.5 text-xs">
        <Switch id="holiday-weekend-shift" checked={shift} onCheckedChange={setShift} />
        <label htmlFor="holiday-weekend-shift" className="cursor-pointer">
          Move off weekends — a Saturday is observed on the Friday before, a Sunday on the Monday
          after. Applied after the shift above.
        </label>
      </div>

      <div className="flex flex-wrap justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="h-9 rounded-lg border border-input px-3 text-sm transition hover:border-ring disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={busy || !name.trim()}
          onClick={() =>
            void onSave({
              name: name.trim(),
              kind,
              month,
              day: kind === "FixedDate" ? day : null,
              ordinal: kind === "NthWeekday" ? ordinal : null,
              weekday: kind === "NthWeekday" ? weekday : null,
              observedOnNearestWeekday: shift,
              offsetDays: offset,
            })
          }
          className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-primary px-3 text-sm font-medium text-primary-foreground transition hover:opacity-90 disabled:opacity-50"
        >
          <Save className="h-4 w-4" aria-hidden />
          {busy ? "Saving…" : existing ? "Save holiday" : "Add holiday"}
        </button>
      </div>
    </div>
  );
}
